import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import VideoContent from "./VideoContent";



const FAQContent = ({ faq }) => {
  const sortedContent = useMemo(() => {
    return faq.content.sort((a, b) => a.order - b.order);
  }, [faq.content]);

  return (
    <div className="p-4 sm:p-6 rounded-lg flex flex-col items-start justify-start h-full text-left overflow-y-auto">
      <h3 className="text-base sm:text-lg md:text-xl font-bold text-blue-900 mb-3 sm:mb-4">
        {faq.title}
      </h3>
      {sortedContent.map((item, index) => {
        switch (item.type) {
          case "description":
            return (
              <p
                key={index}
                className="text-gray-600 mb-3 sm:mb-4 whitespace-pre-line text-xs sm:text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: item.text.trim() }}
              />
            );
          case "list":
            return (
              <ul
                key={index}
                className="list-none space-y-2 sm:space-y-3 mb-3 sm:mb-4 w-full"
              >
                {item.list.map((listItem, liIndex) => (
                  <li
                    key={liIndex}
                    className="flex items-start text-gray-700 text-xs sm:text-sm"
                  >
                    <span className="inline-block w-2 h-2 mt-1.5 sm:mt-2 mr-2 sm:mr-3 rounded-full bg-blue-600 flex-shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: listItem }} />
                  </li>
                ))}
              </ul>
            );
          case "video":
            return (
              <VideoContent
                key={index}
                label={item.label}
                videoUrl={item.video_url}
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

// English FAQs
const ENGLISH_FAQS = [
  {
    title: "What is SPDSSB?",
    content: [
      {
        type: "description",
        text: `<strong>SPDSSB (Strengthening Public Data Systems for Sanitation in Bangladesh)</strong> is a government-led initiative by DPHE, supported by the Bill & Melinda Gates Foundation (BMGF) and managed by the Global Water & Sanitation Center (GWSC). It aims to strengthen sanitation data systems and promote evidence-based decision-making to accelerate progress towards SDG 6.2 in Bangladesh.`,
        order: 1,
      },
    ],
  },
  {
    title:
      "What is NSD, and how does it support national sanitation planning and decision-making?",
    content: [
      {
        type: "description",
        text: `The <strong>National Sanitation Dashboard</strong> (Redesigned and upscaled under the flagship program of the Government of Bangladesh, the Strengthening Public Data System for Sanitation in Bangladesh (SPDSSB) is a tool to display data related to sanitation in an easy-to-understand format for both technical and non-technical audiences. It supports safe waste management practices, facilitates efficient planning, and streamlines survey management and monitoring. By integrating crucial data, advanced analytics, and user-friendly interfaces, the dashboard empowers stakeholders with actionable insights.`,
        order: 1,
      },
    ],
  },
  {
    title:
      "What type of sanitation indicators and data are tracked in the NSD?",
    content: [
      {
        type: "description",
        text: `<strong>The National Sanitation Dashboard (NSD)</strong> tracks 22 Citywide Inclusive Sanitation (CWIS) indicators along with SDG 6.2 indicators. It also features dedicated sections on infrastructure, institutional capacity, as well as indicators related to solid waste management and on-site sanitation, including fecal sludge management.`,
        order: 1,
      },
    ],
  },
  {
    title: "Who can use the National Sanitation Dashboard (NSD)?",
    content: [
      {
        type: "description",
        text: `The National Sanitation Dashboard (NSD) is designed to serve a wide range of users, from government authorities to community members. It provides data and insights that help different stakeholders plan, monitor, and improve sanitation services across the country. The key users include`,
        order: 1,
      },
      {
        type: "list",
        list: [
          "<strong>Regulators and Policymakers:</strong> Government officials tasked with developing sanitation policies and ensuring compliance (e.g., DPHE, LGD).",
          "<strong>City Planners:</strong> Professionals focused on designing and managing sanitation infrastructure in urban areas (e.g., DNCC).",
          "<strong>Community Health Workers:</strong> Field personnel dedicated to enhancing sanitation conditions and monitoring health outcomes.",
          "<strong>Sanitation Service Providers:</strong> Organizations responsible for providing sanitation services and maintaining public infrastructure.",
          "<strong>Donors:</strong> Funding agencies that assess the impact and efficiency of sanitation initiatives to allocate resources effectively.",
          "<strong>Researchers and Analysts:</strong> Academics and consultants who investigate sanitation trends and evaluate program effectiveness.",
          "<strong>Local Government Authorities:</strong> Municipal bodies supervising sanitation services at the local level.",
          "<strong>Citizens:</strong> Community members who use the dashboard to report issues and engage in decision-making.",
        ],
        order: 2,
      },
    ],
  },
  {
    title:
      "How is the 'Multilevel User Management System' managed in the National Sanitation Dashboard (NSD)?",
    content: [
      {
        type: "description",
        text: `The National Sanitation Dashboard system is designed to facilitate controlled access for multiple user types, including both general users and administrators, ensuring that each role has the appropriate level of permissions and access to system functionalities. The system has been built to streamline the process of user registration, approval, and management, providing an efficient solution for handling access requests and permissions for different users of the NSD. This documentation serves as a guide for understanding how the system works, from the user and administrator perspectives.`,
        order: 1,
      },
      {
        type: "video",
        label: `Watch the video to know more:`,
        order: 2,
        video_url: "https://youtu.be/S6kjh_1ijuw?si=MD3zntEQ3cd4eA3z",
      },
    ],
  },
  {
    title:
      "How are surveys planned and managed in the National Sanitation Dashboard (NSD)?",
    content: [
      {
        type: "description",
        text: `In NSD, surveys are planned and managed through a structured process that guides users from project setup and questionnaire design to data upload and visualization in the City Profile section. This approach ensures that local sanitation data is collected in a consistent and accurate way, making it easier to analyze and use for evidence-based planning, monitoring, and decision-making across different governance levels.`,
        order: 1,
      },
      {
        type: "video",
        label: `Watch the video to know more:`,
        order: 2,
        video_url:
          "https://www.youtube.com/watch?v=mM0ss1M4mso&list=RDmM0ss1M4mso&start_radio=1",
      },
    ],
  },
];

// Bengali FAQs
const BENGALI_FAQS = [
  {
    title: "SPDSSB কী?",
    content: [
      {
        type: "description",
        text: `<strong>SPDSSB (Strengthening Public Data Systems for Sanitation in Bangladesh)</strong> হলো DPHE-এর নেতৃত্বে পরিচালিত একটি সরকারি উদ্যোগ, যা Bill & Melinda Gates Foundation (BMGF)-এর সহায়তায় এবং Global Water & Sanitation Center (GWSC)-এর ব্যবস্থাপনায় বাস্তবায়িত হচ্ছে। এর লক্ষ্য হলো স্যানিটেশন সংক্রান্ত তথ্যব্যবস্থা শক্তিশালী করা এবং প্রমাণভিত্তিক সিদ্ধান্ত গ্রহণকে উৎসাহিত করে বাংলাদেশে SDG 6.2 লক্ষ্য অর্জনের গতি বাড়ানো।`,
        order: 1,
      },
    ],
  },
  {
    title:
      "ন্যাশনাল স্যানিটেশন ড্যাশবোর্ড (NSD) কী এবং এটি কীভাবে জাতীয় স্যানিটেশন পরিকল্পনা ও সিদ্ধান্ত গ্রহণে সহায়তা করে?",
    content: [
      {
        type: "description",
        text: `<strong>ন্যাশনাল স্যানিটেশন ড্যাশবোর্ড (NSD)</strong> হলো বাংলাদেশ সরকারের <strong>Strengthening Public Data Systems for Sanitation in Bangladesh (SPDSSB)</strong> কর্মসূচির আওতায় পুনর্নির্মিত ও সম্প্রসারিত একটি ডিজিটাল প্ল্যাটফর্ম, যা স্যানিটেশন সম্পর্কিত তথ্য সহজভাবে উপস্থাপন করে---যাতে প্রযুক্তিগত ও অ-প্রযুক্তিগত উভয় ব্যবহারকারীর জন্য তা বোধগম্য হয়। এটি নিরাপদ বর্জ্য ব্যবস্থাপনা, কার্যকর পরিকল্পনা, এবং জরিপ পরিচালনা ও তদারকি সহজ করে। গুরুত্বপূর্ণ তথ্য, উন্নত বিশ্লেষণ এবং ব্যবহারবান্ধব ইন্টারফেস একত্রিত করে, NSD অংশীজনদেরকে প্রমাণভিত্তিক সিদ্ধান্ত গ্রহণে সহায়তা করে, যা দেশের স্যানিটেশন পরিষেবা উন্নয়নে ভূমিকা রাখে।`,
        order: 1,
      },
    ],
  },
  {
    title: "NSD-তে কী ধরনের স্যানিটেশন সূচক ও তথ্য অনুসরণ করা হয়?",
    content: [
      {
        type: "description",
        text: `<strong>ন্যাশনাল স্যানিটেশন ড্যাশবোর্ড (NSD)</strong>-এ ২২টি <strong>Citywide Inclusive Sanitation (CWIS)</strong> সূচকসহ <strong>SDG 6.2</strong> সম্পর্কিত সূচকগুলো পর্যবেক্ষণ করা হয়। এছাড়াও এতে অবকাঠামো, প্রাতিষ্ঠানিক সক্ষমতা, কঠিন বর্জ্য ব্যবস্থাপনা এবং অনসাইট স্যানিটেশনসহ ফিকাল স্লাজ ম্যানেজমেন্ট সম্পর্কিত সূচকগুলোর জন্য পৃথক সেকশন অন্তর্ভুক্ত রয়েছে।`,
        order: 1,
      },
    ],
  },
  {
    title: "ন্যাশনাল স্যানিটেশন ড্যাশবোর্ড (NSD) কে ব্যবহার করতে পারে?",
    content: [
      {
        type: "description",
        text: `<strong>ন্যাশনাল স্যানিটেশন ড্যাশবোর্ড (NSD)</strong> এমনভাবে তৈরি করা হয়েছে যাতে এটি সরকারি সংস্থা থেকে শুরু করে সাধারণ জনগণ পর্যন্ত বিভিন্ন ব্যবহারকারীর জন্য কার্যকর হয়। এটি এমন তথ্য ও বিশ্লেষণ সরবরাহ করে যা বিভিন্ন অংশীজনকে দেশের স্যানিটেশন সেবা পরিকল্পনা, পর্যবেক্ষণ ও উন্নত করতে সহায়তা করে।<br/><br/>মূল ব্যবহারকারীরা হলেন:`,
        order: 1,
      },
      {
        type: "list",
        list: [
          "<strong>নিয়ন্ত্রক ও নীতিনির্ধারকগণ:</strong> স্যানিটেশন নীতি প্রণয়ন ও বাস্তবায়নের দায়িত্বপ্রাপ্ত সরকারি কর্মকর্তা (যেমন: DPHE, LGD)।",
          "<strong>সিটি পরিকল্পনাবিদগণ:</strong> যারা শহরের স্যানিটেশন অবকাঠামো নকশা ও ব্যবস্থাপনায় কাজ করেন (যেমন: DNCC)।",
          "<strong>কমিউনিটি হেলথ ওয়ার্কাররা:</strong> যারা মাঠপর্যায়ে স্যানিটেশন অবস্থা উন্নত করা ও স্বাস্থ্যগত ফলাফল পর্যবেক্ষণে কাজ করেন।",
          "<strong>স্যানিটেশন সেবা প্রদানকারীরা:</strong> যেসব সংস্থা স্যানিটেশন সেবা প্রদান ও জনপরিকাঠামো রক্ষণাবেক্ষণের দায়িত্বে রয়েছে।",
          "<strong>দাতা সংস্থাগুলো:</strong> যারা স্যানিটেশন কার্যক্রমের প্রভাব ও দক্ষতা মূল্যায়ন করে কার্যকরভাবে সম্পদ বণ্টন করে।",
          "<strong>গবেষক ও বিশ্লেষকগণ:</strong> যারা স্যানিটেশন প্রবণতা বিশ্লেষণ ও প্রকল্পের কার্যকারিতা মূল্যায়ন করেন।",
          "<strong>স্থানীয় সরকারি কর্তৃপক্ষ:</strong> পৌর প্রতিষ্ঠানসমূহ, যারা স্থানীয় পর্যায়ে স্যানিটেশন সেবা তদারকি করে।",
          "<strong>নাগরিকবৃন্দ:</strong> সাধারণ জনগণ, যারা সমস্যা রিপোর্ট করতে ও সিদ্ধান্ত গ্রহণ প্রক্রিয়ায় অংশগ্রহণ করতে ড্যাশবোর্ডটি ব্যবহার করেন।",
        ],
        order: 2,
      },
    ],
  },
  {
    title:
      "ন্যাশনাল স্যানিটেশন ড্যাশবোর্ড (NSD)-এ 'মাল্টিলেভেল ইউজার ম্যানেজমেন্ট সিস্টেম' কীভাবে পরিচালিত হয়?",
    content: [
      {
        type: "description",
        text: `ন্যাশনাল স্যানিটেশন ড্যাশবোর্ড এমনভাবে তৈরি করা হয়েছে যাতে বিভিন্ন ধরনের ব্যবহারকারীর জন্য নিয়ন্ত্রিত প্রবেশাধিকার নিশ্চিত করা যায়---যার মধ্যে সাধারণ ব্যবহারকারী ও প্রশাসক উভয়ই অন্তর্ভুক্ত। প্রতিটি ব্যবহারকারীর ভূমিকা অনুযায়ী প্রয়োজনীয় অনুমতি ও সিস্টেম ফিচারে প্রবেশাধিকার নির্ধারিত থাকে।<br/><br/>এই সিস্টেমটি ব্যবহারকারী নিবন্ধন, অনুমোদন ও ব্যবস্থাপনা প্রক্রিয়াকে সহজতর করে, ফলে NSD-এর বিভিন্ন ব্যবহারকারীর অ্যাক্সেস রিকোয়েস্ট ও অনুমতি পরিচালনা আরও কার্যকরভাবে করা যায়।`,
        order: 1,
      },
      {
        type: "video",
        label: `👉 বিস্তারিত জানতে ভিডিওটি দেখুন।`,
        order: 2,
        video_url: "https://youtu.be/S6kjh_1ijuw?si=MD3zntEQ3cd4eA3z",
      },
    ],
  },
  {
    title: "NSD-এ জরিপ কীভাবে পরিকল্পনা ও পরিচালনা করা হয়?",
    content: [
      {
        type: "description",
        text: `NSD-এ জরিপগুলো একটি নির্ধারিত প্রক্রিয়ার মাধ্যমে পরিকল্পনা ও পরিচালিত হয়, যা ব্যবহারকারীদের প্রকল্প তৈরি, প্রশ্নমালা নকশা, তথ্য আপলোড এবং সিটি প্রোফাইল সেকশনে ভিজ্যুয়ালাইজেশন পর্যন্ত ধাপে ধাপে নির্দেশনা দেয়। এই পদ্ধতিটি স্থানীয় স্যানিটেশন তথ্যকে নির্ভুল ও একরূপভাবে সংগ্রহ নিশ্চিত করে, ফলে তা বিশ্লেষণ করা এবং প্রমাণভিত্তিক পরিকল্পনা, পর্যবেক্ষণ ও সিদ্ধান্ত গ্রহণে ব্যবহার করা আরও সহজ হয়।`,
        order: 1,
      },
      {
        type: "video",
        label: `👉 বিস্তারিত জানতে ভিডিওটি দেখুন।`,
        order: 2,
        video_url:
          "https://www.youtube.com/watch?v=mM0ss1M4mso&list=RDmM0ss1M4mso&start_radio=1",
      },
    ],
  },
];

const FAQ = () => {
  const { t, i18n } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Select FAQ data based on current language
  const currentFAQs = i18n.language === 'bn' ? BENGALI_FAQS : ENGLISH_FAQS;
  const activeFAQ = currentFAQs[activeIndex];

  return (
    <div className="w-full py-8 sm:py-12 md:py-16 px-4 sm:px-[6%] bg-white mt-10 sm:mt-16 md:mt-20">
      <div className="border-[#2F4B7C80] border-[1px] sm:border-[2px] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12">
        <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-center text-blue-900 mb-6 sm:mb-8 md:mb-10 px-2">
          {t("new_home.faq_title") || "Frequently Asked Questions (FAQ)"}
        </h2>

        <div className="relative flex justify-center">
          <div className="flex w-full max-w-[1200px] flex-col lg:flex-row lg:space-x-6 xl:space-x-8">
            {/* Left Panel: FAQ Titles/Tabs */}
            <div className="w-full lg:w-[45%] xl:w-[40%] mb-6 lg:mb-0 z-10">
              <ul className="list-none p-0 bg-[#ffffff] border-[.5px] rounded-lg overflow-hidden">
                {currentFAQs.map((faq, index) => (
                  <li
                    key={index}
                    className={`flex items-center justify-between py-2 sm:py-3 px-2 sm:px-3 cursor-pointer transition-colors ${
                      index !== 0 ? "border-t-[.5px]" : ""
                    } ${
                      index === activeIndex
                        ? "bg-[#FAFBFF]"
                        : "hover:bg-[#FAFBFF]"
                    }`}
                    onClick={() => setActiveIndex(index)}
                  >
                    <div className="flex items-center w-full">
                      <span
                        className={`inline-block w-2 h-2 sm:w-3 sm:h-3 rounded-full mr-2 sm:mr-3 md:mr-4 flex-shrink-0 self-start translate-y-1 sm:translate-y-1.5 ${
                          index === activeIndex ? "bg-blue-700" : "bg-gray-400"
                        }`}
                      />
                      <span className="text-xs sm:text-sm md:text-base font-medium text-left flex-1 pr-2">
                        {faq.title}
                      </span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className={`w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 flex-shrink-0 ${
                          index === activeIndex
                            ? "text-blue-700"
                            : "text-gray-400"
                        }`}
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Panel: Active FAQ Content */}
            <div className="w-full lg:w-[55%] xl:w-[60%] bg-[#FAFBFF] rounded-2xl border-[.5px] min-h-[300px] sm:min-h-[400px] lg:min-h-[500px] max-h-[600px] lg:-translate-x-[54px] pl-6">
              {activeFAQ && <FAQContent faq={activeFAQ} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;